import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MiniGameService } from '../../../_services/mini-game.service';
import { GameAudioService } from '../../../_services/game-audio.service';
import { ImmersiveGameBase } from '../_shared/immersive-game.base';
import { GlobalService } from 'src/app/_services/global.service';
import { MiniGameSession, MiniGameStudent } from '../../../interfaces/mini-game.interface';
import { TrekRound, FractionTrekState } from '../../../interfaces/fraction-trek.interface';
import { FractionTrekStageComponent } from './fraction-trek-stage.component';

interface MixedParts { whole: number; num: number; den: number; }

/**
 * Host shell for Fraction Trek. Reads `FractionTrekState`, renders the
 * `A op B` prompt as mixed numbers, drives the stage's CHECK via
 * @ViewChild, and writes outcomes with `scoreFractionTrekRound` +
 * `advanceFractionTrekRound`. On success a compact equation banner
 * (`A op B = C`) shows at the top so the number line stays clear.
 */
@Component({
  selector: 'app-fraction-trek-game',
  templateUrl: './fraction-trek-game.component.html',
  styleUrls: ['./fraction-trek-game.component.scss']
})
export class FractionTrekGameComponent extends ImmersiveGameBase implements OnInit, OnDestroy {
  @ViewChild(FractionTrekStageComponent) stage?: FractionTrekStageComponent;

  private destroy$ = new Subject<void>();
  currLang: any = 'en';

  session: MiniGameSession | null = null;

  showSuccess = false;
  resolving = false;
  lastXp = 0;

  private prevRound = -1;
  private pendingTimeouts: any[] = [];

  constructor(
    private miniGameService: MiniGameService,
    private gameAudioService: GameAudioService,
    private globalService: GlobalService
  ) {
    super();
  }

  ngOnInit(): void {
    this.globalService.getLang()
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => this.currLang = lang);

    this.miniGameService.session$
      .pipe(takeUntil(this.destroy$))
      .subscribe(s => {
        this.session = s;
        const round = this.state?.currentRound ?? -1;
        if (round !== this.prevRound) {
          this.prevRound = round;
          this.showSuccess = false;
          this.resolving = false;
        }
      });

    this.miniGameService.lastResult$
      .pipe(takeUntil(this.destroy$))
      .subscribe(r => { if (r) this.lastXp = r.xpEarned; });
  }

  ngOnDestroy(): void {
    this.pendingTimeouts.forEach(t => clearTimeout(t));
    this.pendingTimeouts = [];
    this.destroy$.next();
    this.destroy$.complete();
  }

  private later(fn: () => void, ms: number): void {
    this.pendingTimeouts.push(setTimeout(fn, ms));
  }

  // ── Derived state ──────────────────────────────────────────────────

  get state(): FractionTrekState | null {
    return (this.session?.gameState as FractionTrekState) ?? null;
  }
  get round(): TrekRound | null {
    const s = this.state;
    if (!s) return null;
    return s.rounds[s.currentRound] ?? null;
  }
  get currentRound(): number { return this.state?.currentRound ?? 0; }
  get totalRounds(): number { return this.state?.totalRounds ?? 0; }
  get roundDots(): number[] { return Array.from({ length: this.totalRounds }, (_, i) => i); }
  get solvedFlags(): boolean[] { return this.state?.solved ?? []; }

  get student(): MiniGameStudent | null {
    const s = this.state;
    if (!s || s.currentStudentId == null) return null;
    return this.session?.students.find(st => st.id === s.currentStudentId) ?? null;
  }
  get studentName(): string {
    const st = this.student;
    if (!st) return '';
    return (this.currLang === 'ar' ? st.name.ar : st.name.en || '').split(' ')[0];
  }

  // ── Prompt / equation helpers ──────────────────────────────────────

  private mixed(dths: number): MixedParts {
    const den = this.round?.den ?? 1;
    return { whole: Math.floor(dths / den), num: dths % den, den };
  }
  get aParts(): MixedParts | null { return this.round ? this.mixed(this.round.aDths) : null; }
  get bParts(): MixedParts | null { return this.round ? this.mixed(this.round.bDths) : null; }
  get opSign(): string { return this.round?.op === '-' ? '−' : '+'; }

  private label(dths: number): string {
    const m = this.mixed(dths);
    if (m.num === 0) return `${m.whole}`;
    if (m.whole === 0) return `${m.num}/${m.den}`;
    return `${m.whole} ${m.num}/${m.den}`;
  }
  get equationLabel(): string {
    if (!this.round) return '';
    return `${this.label(this.round.aDths)} ${this.opSign} ${this.label(this.round.bDths)} = ${this.label(this.round.answerDths)}`;
  }

  // ── Controls ───────────────────────────────────────────────────────

  onCheck(): void {
    if (this.resolving || !this.stage?.canCheck) return;
    this.gameAudioService.play('assets/game-mode/sound-effects/fastSoundEffect.mp3');
    this.stage.check();
  }

  onSolved(): void {
    if (this.resolving) return;
    this.resolving = true;
    this.gameAudioService.play('assets/game-mode/sound-effects/correctSoundEffect.mp3');
    this.miniGameService.scoreFractionTrekRound(true);
    this.showSuccess = true;
    this.later(() => this.miniGameService.advanceFractionTrekRound(), 3000);
  }

  onMissed(): void {
    if (this.resolving) return;
    // Wrong spot — buzz and let them re-drag. Nothing revealed.
    this.gameAudioService.play('assets/game-mode/sound-effects/buzzerSoundEffect.mp3');
  }

  skip(): void {
    if (this.resolving) return;
    this.resolving = true;
    this.miniGameService.scoreFractionTrekRound(false);
    this.later(() => this.miniGameService.advanceFractionTrekRound(), 250);
  }
}
